import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import AuthShell from '../components/AuthShell';
import GoogleSignInButton from '../components/GoogleSignInButton';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState(location.state?.email || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error('Please enter email and password');
      return;
    }

    setLoading(true);
    try {
      const data = await api('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email: email.toLowerCase().trim(), password }),
      });

      await login(data.token, data.user);

      if (!data.email_verified) {
        navigate('/app/verify', { state: { email: email.toLowerCase().trim() } });
      } else if (!data.user.profile_complete) {
        navigate('/app/setup');
      } else {
        navigate('/app/discover');
      }
    } catch (err) {
      toast.error(err.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthShell>
      <h1 className="text-4xl font-serif font-bold text-[#2B2D42]" style={{ fontFamily: 'Playfair Display, serif' }}>
        Welcome Back
      </h1>
      <p className="text-[#6D6875] mt-2 mb-8">Log in to continue your journey</p>

      <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
        <div>
          <label className="block text-sm font-bold text-[#2B2D42] mb-2">Email Address</label>
          <div className="flex items-center bg-white border border-[#F4F1DE] rounded-lg px-4 py-3 focus-within:border-[#E07A5F] transition-colors">
            <Mail className="w-5 h-5 text-[#B5B5B5]" />
            <input
              type="email"
              data-testid="login-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              className="flex-1 ml-3 outline-none bg-transparent text-[#2B2D42] placeholder-[#B5B5B5]"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-bold text-[#2B2D42] mb-2">Password</label>
          <div className="flex items-center bg-white border border-[#F4F1DE] rounded-lg px-4 py-3 focus-within:border-[#E07A5F] transition-colors">
            <Lock className="w-5 h-5 text-[#B5B5B5]" />
            <input
              type={showPassword ? 'text' : 'password'}
              data-testid="login-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              autoComplete="current-password"
              className="flex-1 ml-3 outline-none bg-transparent text-[#2B2D42] placeholder-[#B5B5B5]"
            />
            <button
              type="button"
              data-testid="login-toggle-password"
              onClick={() => setShowPassword(!showPassword)}
              className="text-[#B5B5B5] hover:text-[#6D6875]"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <div className="text-right">
          <Link
            to="/app/forgot-password"
            data-testid="login-forgot-password-link"
            className="text-sm font-semibold text-[#C8553D] hover:text-[#9E3A26]"
          >
            Forgot Password?
          </Link>
        </div>

        <button
          type="submit"
          data-testid="login-submit-button"
          disabled={loading}
          className="w-full bg-[#C8553D] hover:bg-[#9E3A26] disabled:opacity-60 text-white font-bold py-4 rounded-full transition-colors flex items-center justify-center gap-2 shadow-lg shadow-[#C8553D]/30"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Log In'}
        </button>
      </form>

      {/* Divider */}
      <div className="my-6 flex items-center gap-3">
        <div className="h-px flex-1 bg-[#F4F1DE]" />
        <span className="text-xs font-bold uppercase tracking-wider text-[#B5B5B5]">or</span>
        <div className="h-px flex-1 bg-[#F4F1DE]" />
      </div>

      <GoogleSignInButton testId="login-google-button" />

      <div className="mt-8 text-center">
        <span className="text-sm text-[#6D6875]">Don't have an account? </span>
        <Link
          to="/app/signup"
          data-testid="login-signup-link"
          className="text-sm font-bold text-[#C8553D] hover:text-[#9E3A26]"
        >
          Sign up
        </Link>
      </div>
    </AuthShell>
  );
}
